/* eslint-disable react/function-component-definition */
import { forwardRef } from "react";
import Button from "./Button.component";
import type {
  ButtonHandles,
  StandardButtonProps,
  Type,
  Size,
  Justify,
} from "@jnctn/bridge-design-system/types/StandardButton";
import cx from "classnames";
import nextStyles from "./StandardButton.module.css";
import enterpriseStyles from "./StandardButtonEnterprise.module.css";
import Loader from "../Loader/Loader.component";
import { useButtonVariant } from "../ThemeProvider/useButtonVariant";

export type { StandardButtonProps, ButtonHandles, Size, Type, Justify };

const UnforwardedStandardButton = (
  {
    className,
    icon,
    rightIcon,
    overrideIconClassName = false,
    type = "primary",
    shape = "default-shape",
    size = "medium",
    loading = false,
    disabled,
    children,
    submit,
    justify = "center",
    labelHidden,
    ariaLabel,
    ...props
  }: StandardButtonProps,
  ref
) => {
  const buttonVariant = useButtonVariant();
  const styles = buttonVariant === "enterprise" ? enterpriseStyles : nextStyles;
  return (
    <Button
      ref={ref}
      {...props}
      submit={submit}
      disabled={disabled || loading}
      labelHidden={labelHidden}
      ariaLabel={ariaLabel}
      icon={
        icon ? (
          <span
            className={cx("flex items-center", {
              [styles.icon]: !overrideIconClassName,
              "mr-2": !labelHidden,
            })}
          >
            {icon}
          </span>
        ) : undefined
      }
      className={cx(
        [styles.root],
        {
          [styles.extraLarge]: size === "extra-large",
          [styles.large]: size === "large",
          [styles.medium]: size === "medium",
          [styles.small]: size === "small",
          [styles.extraSmall]: size === "extra-small",
          [styles.justifyStart]: justify === "start",
          [styles.justifyCenter]: justify === "center",
          [styles.justifyEnd]: justify === "end",
          [styles.primary]: type === "primary",
          [styles.fabSecondary]: type === "fabSecondary",
          [styles.secondary]: type === "secondary",
          [styles.brandSecondary]: type === "brandSecondary",
          [styles.outline]: type === "outline",
          [styles.transparent]: type === "transparent",
          [styles.warning]: type === "warning",
          [styles.destructive]: type === "destructive",
          [styles.destructiveOutline]: type === "destructiveOutline",
          [styles.hero]: type === "hero",
          [styles.transparentPrimary]: type === "transparentPrimary",
          [styles.transparentDestructive]: type === "transparentDestructive",
          [styles.leftInput]: shape === "left-input",
          [styles.defaultShape]: shape === "default-shape",
          "leading-2": loading,
        },
        "inline-flex",
        className
      )}
      rightIcon={
        rightIcon ? (
          <span
            className={cx("ml-2 flex items-center", {
              [styles.icon]: !overrideIconClassName,
            })}
          >
            {rightIcon}
          </span>
        ) : undefined
      }
    >
      {loading ? (
        <>
          <Loader
            color={
              type === "secondary" || type === "outline" ? "black" : "white"
            }
            size="medium"
            className="absolute"
            centered={false}
          />
          <i className="invisible">{props.label}</i>
        </>
      ) : (
        children
      )}
    </Button>
  );
};

const StandardButton = forwardRef(UnforwardedStandardButton);

export default StandardButton;
